import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Grid2,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { LockOutlined, LockOpenOutlined } from "@mui/icons-material";
import { EventSelect, ImageUpload, PlayerSelect, TranslationEditor } from "components";
import ListSection from "components/ListSection";
import { KozoomEvent, KozoomPlayer } from "constants/types";
import moment from "moment";
import { DetailHeading } from "pages/Common";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "store";
import { getGameName } from "utils";
import { formatEventDate } from "utils/time";
import { useVideoDetail } from "./hooks";
import VideoPreview from "./VideoPreview";
import VideoUpload from "./VideoUpload";
import apiEndpoints from "constants/endpoints";

export const VideoDetail = () => {
  const {
    loading,
    saving,
    videoId,
    video,
    updateVideo,
    onSaveVideo,
  } = useVideoDetail();
  const gamesById = useAppSelector(state => state.games.gamesById);
  const getEventName = useCallback((option: KozoomEvent) => {
    return `${option.id}, ${getGameName(gamesById, option.gameId)}, ${option.eventTypeName}, ${option.city}`
  }, [gamesById]);
  
  const videoTitle = useMemo(() => {
    return video.i18n?.find(item => item.lang === 'en')?.description || '';
  }, [video]);
  const [isVideoSrcUrlUnlocked, setIsVideoSrcUrlUnlocked] = useState(false);

  const pendingStatusRef = useRef(false);
  useEffect(() => {
    if (video.status === 'pending') {
      pendingStatusRef.current = true;
    }
  }, [video.status]);;

  if (loading && !video.id) {
    return (
      <Paper>
        <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (!video.id && videoId !== 'new') {
    return null;
  }

  return (
    <>
      <Paper>
        <Box p={2}>
          <DetailHeading
            listLink={apiEndpoints.videos}
            listName="Videos"
            title={video.livescoreImportId && videoId === 'new' ? `Import Video with Ref ${video.livescoreImportId}` : videoId === 'new' ? "New Video" : `Video ${videoId}`}
            saving={saving}
            onSave={onSaveVideo}
          />
          <Grid2 container spacing={2} mb={2}>
            <Grid2 size={{ lg: 4, md: 6, sm: 12 }}>
              <Paper variant="outlined">
                <Box p={2}>
                  <EventSelect
                    eventId={video.eventId || null}
                    disabled={videoId !== 'new'}
                    placeholder="Main Event"
                    params={{
                      startBefore: moment().format('Y-MM-DD'),
                    }}
                    onChange={(event: KozoomEvent) => updateVideo("event", event)}
                  />
                  <Box mt={2}>
                    <ListSection
                      getItemRouterLink={(event: KozoomEvent) => `/${apiEndpoints.eventVideos}/${event.id}`}
                      sectionTitle="Playlists"
                      modalTitle="Select a playlist"
                      noItemsMessage="There are no events for this video"
                      items={video.events}
                      selectComponent={EventSelect}
                      onUpdate={(events: KozoomEvent[]) => updateVideo("events", events)}
                      getPrimaryLabel={getEventName}
                      getSecondaryLabel={(event: KozoomEvent) => `(${formatEventDate(event.startTime)} - ${formatEventDate(event.endTime)})`} modalEditTitle={undefined} apiPath={undefined} dynamicInputs={undefined} />
                  </Box>
                  <Box>
                    <ListSection
                      getItemRouterLink={(player: KozoomPlayer) => `/${apiEndpoints.players}/${player.id}`}
                      sectionTitle="Players"
                      modalTitle="Select a player"
                      noItemsMessage="There are no players linked to this video"
                      items={video.players}
                      selectComponent={PlayerSelect}
                      onUpdate={(players: KozoomPlayer[]) => updateVideo("players", players)}
                      getPrimaryLabel={(player: KozoomPlayer) => `${player.firstName} ${player.lastName}`} modalEditTitle={undefined} getSecondaryLabel={undefined} apiPath={undefined} dynamicInputs={undefined} />
                  </Box>
                  <Box mt={2}>
                    <FormControlLabel control={
                      <Switch
                        checked={Boolean(video.showInNewsFeed)}
                        onChange={(e) => updateVideo('showInNewsFeed', e.target.checked)}
                      />
                    } label="Show in News Feed" />
                  </Box>
                </Box>
              </Paper>
            </Grid2>
            <Grid2 size={{ lg: 4, md: 6, sm: 12 }}>
              <ImageUpload
                label="Thumbnail"
                path={`uploads/events/${video.eventId}/videos`}
                fileName={video.thumbnail}
                uploadedUrl={video.thumbnail?.startsWith('http') ? video.thumbnail : undefined}
                sourceUrl={video.thumbnailSrcUrl}
                disabled={!video.eventId}
                onUploaded={(fileName) => updateVideo("thumbnail", fileName)}
              />
              {videoId === 'new' &&
                <Alert severity="info" sx={{mt: 2}}>
                  Uploading thumbnails is optional. Thumbnails can be generated automatically.
                </Alert>
              }
            </Grid2>
            <Grid2 size={{ lg: 4, md: 6, sm: 12 }}>
              <TextField
                fullWidth
                label="Video Source URL"
                placeholder="Paste video source URL"
                value={video.videoSrcUrl || ''}
                onChange={(e) => updateVideo('videoSrcUrl', e.target.value)}
                disabled={!isVideoSrcUrlUnlocked}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={isVideoSrcUrlUnlocked ? 'Lock input' : 'Unlock input'}>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => setIsVideoSrcUrlUnlocked((value) => !value)}
                            aria-label={isVideoSrcUrlUnlocked ? 'Lock video source URL field' : 'Unlock video source URL field'}
                          >
                            {isVideoSrcUrlUnlocked ? <LockOpenOutlined /> : <LockOutlined />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Box mt={2}>
              {videoId === 'new' && (
                <VideoUpload 
                  videoSrcUrl={video.videoSrcUrl}
                  disabled={!video.eventId}
                  eventId={video.eventId || 0}
                  videoTitle={videoTitle}
                  onVideoDurationChange={(duration) => updateVideo("duration", Math.round(duration))}
                  onUploaded={({url, bunnyVideoId}) => {
                    if (url) {
                      updateVideo("url", url);
                    } else if (bunnyVideoId) {
                      updateVideo("bunnyVideoId", bunnyVideoId);
                    }
                  }}
                />
              )}
              </Box>
              {videoId !== 'new' &&
                <>
                  <VideoPreview
                    url={video.url || video.baseVideoUrl}
                    videoSrcUrl={video.videoSrcUrl}
                    duration={video.duration}
                  />
                  {pendingStatusRef.current &&
                    <Box mt={2}>
                      <Alert severity="warning">The video is being transcoded. It will be automatically published the video when the process is done. Use the switch below to publish the video manually when needed.</Alert>
                      <Switch color="success" onChange={(e) => updateVideo('status', 'published') } />
                    </Box>
                  }
                </>
              }
            </Grid2>
          </Grid2>
          <Paper variant="outlined">
            <Box p={2}>
              <Alert severity="info">
                <strong>English</strong> is the default content. If there are any missing translations, they will be <strong>replaced with the English version automatically</strong> when saving.
              </Alert>
              {video.i18n && (
                <TranslationEditor
                  i18n={video.i18n}
                  inputs={[
                    { field: 'description', type: 'multiline', label: 'Description' },
                    { field: 'article', type: 'richtext', label: 'Article for Masterclass' },
                  ]}
                  onUpdate={i18n => updateVideo("i18n", i18n)} />
              )}
            </Box>
          </Paper>
        </Box>
      </Paper>
    </>
  );
};

